/**
 * Level 20: The Nexus (True Final Challenge)
 * 
 * The ultimate level combining every mechanic and every enemy type.
 * A cosmic dimension-bending arena that tests mastery of all skills.
 * Features a dramatic multi-phase layout with escalating difficulty.
 * 
 * Design philosophy: Victory lap showcase. Each phase references 
 * a previous world theme while combining multiple enemy types.
 * Generous but well-placed checkpoints (trampolines/portals).
 * 
 * @module configs/levels/Level20_TheNexus
 */

/** @type {import('../LevelTypes').LevelConfig} */
const Level20_TheNexus = {
    id: 20,
    name: 'The Nexus',
    subtitle: 'Convergence',
    description: 'The ultimate test. All mechanics. All enemies. One final challenge.',

    worldWidth: 6800,
    worldHeight: 2800,
    playerStart: { x: 150, y: 2500 },
    gravity: 0.9,

    theme: {
        bgGradient: ['#0a0020', '#200040'],
        platformColor: 0x6a0dad,
        accentColor: 0x00ffff,
        particleColor: 0xff00ff,
        ambient: 'nexus'
    },

    platforms: [
        // ═══ PHASE 1: Nexus Entry (Nature echo) ═══
        { x: 0, y: 2600, w: 450, h: 60 },
        { x: 550, y: 2450, w: 180, h: 25 },
        { x: 800, y: 2300, w: 200, h: 30 },
        { x: 1050, y: 2150, w: 250, h: 35 },

        // ═══ PHASE 2: Fortress Echo ═══
        { x: 1350, y: 2350, w: 300, h: 45 },
        { x: 1700, y: 2200, w: 250, h: 35 },
        { x: 2000, y: 2050, w: 200, h: 28 },

        // ═══ PHASE 3: Volcanic Echo ═══
        { x: 2250, y: 2250, w: 300, h: 40 },
        { x: 2600, y: 2100, w: 250, h: 35 },
        { x: 2900, y: 1950, w: 200, h: 30 },

        // ═══ PHASE 4: Sky Echo ═══
        { x: 3150, y: 2100, w: 250, h: 35 },
        { x: 3450, y: 1900, w: 200, h: 28 },
        { x: 3700, y: 1700, w: 300, h: 40 },

        // ═══ PHASE 5: Convergence Climb ═══
        { x: 4000, y: 1900, w: 200, h: 28 },
        { x: 3900, y: 1700, w: 180, h: 25 },
        { x: 4100, y: 1500, w: 200, h: 30 },
        { x: 3950, y: 1300, w: 180, h: 25 },
        { x: 4150, y: 1100, w: 200, h: 28 },

        // ═══ PHASE 6: Nexus Core ═══
        { x: 4400, y: 1300, w: 350, h: 50 },
        { x: 4800, y: 1150, w: 250, h: 35 },
        { x: 5100, y: 1000, w: 200, h: 28 },
        { x: 5350, y: 850, w: 300, h: 40 },

        // ═══ PHASE 7: Final Ascent ═══
        { x: 5700, y: 1050, w: 200, h: 30 },
        { x: 5600, y: 850, w: 180, h: 25 },
        { x: 5800, y: 650, w: 250, h: 35 },
        { x: 6050, y: 500, w: 200, h: 28 },

        // ═══ VICTORY PLATFORM ═══
        { x: 6300, y: 700, w: 500, h: 60 }
    ],

    movingPlatforms: [
        // Phase 1
        { x: 500, y: 2520, w: 120, h: 20, moveY: 130, speed: 0.002 },
        // Phase 2
        { x: 1550, y: 2280, w: 130, h: 20, moveX: 140, speed: 0.003 },
        { x: 1900, y: 2120, w: 110, h: 18, moveY: 120, speed: 0.0025 },
        // Phase 3
        { x: 2450, y: 2170, w: 120, h: 20, moveX: 130, speed: 0.003 },
        { x: 2800, y: 2020, w: 110, h: 18, moveY: 140, speed: 0.0025 },
        // Phase 4
        { x: 3350, y: 1970, w: 120, h: 20, moveX: 100, speed: 0.0035 },
        // Phase 5
        { x: 4050, y: 1400, w: 100, h: 18, moveY: 150, speed: 0.002 },
        { x: 4000, y: 1200, w: 110, h: 18, moveX: 120, speed: 0.003 },
        // Phase 6
        { x: 4700, y: 1220, w: 130, h: 20, moveX: 100, speed: 0.003 },
        { x: 5200, y: 920, w: 110, h: 18, moveY: 100, speed: 0.0025 },
        // Phase 7
        { x: 5700, y: 950, w: 120, h: 20, moveY: 160, speed: 0.002 },
        // Final
        { x: 6150, y: 600, w: 140, h: 20, moveX: 130, speed: 0.0035 }
    ],

    spikes: [
        // Phase 1
        { x: 1100, y: 2150 },
        { x: 1150, y: 2150 },
        // Phase 2
        { x: 1400, y: 2350 },
        { x: 1750, y: 2200 },
        // Phase 3
        { x: 2300, y: 2250 },
        { x: 2650, y: 2100 },
        { x: 2700, y: 2100 },
        // Phase 4
        { x: 3200, y: 2100 },
        { x: 3750, y: 1700 },
        // Phase 5
        { x: 4050, y: 1500 },
        { x: 4200, y: 1100 },
        // Phase 6
        { x: 4450, y: 1300 },
        { x: 4850, y: 1150 },
        { x: 5400, y: 850 },
        // Final
        { x: 6350, y: 700 },
        { x: 6400, y: 700 }
    ],

    stars: [
        { x: 800, y: 2200 },
        { x: 1700, y: 2100 },
        { x: 2600, y: 2000 },
        { x: 3700, y: 1600 },
        { x: 4150, y: 1000 },
        { x: 5100, y: 900 },
        { x: 5800, y: 550 },
        { x: 6500, y: 600 }
    ],

    enemies: [
        // ═══ Phase 1: Flying introduction ═══
        { x: 650, y: 2200, type: 'flying', options: { amplitude: 60, speed: 1.3, walkDist: 120, color: 0x66ff99 } },
        { x: 900, y: 2100 },

        // ═══ Phase 2: Shield guardians ═══
        { x: 1450, y: 2300, type: 'shield', options: { speed: 1.2, walkDist: 100, slamInterval: 2500, shieldColor: 0xff6600 } },
        { x: 1800, y: 2150, type: 'shield', options: { speed: 1.0, walkDist: 80, slamInterval: 3000 } },

        // ═══ Phase 3: Explosive territory ═══
        { x: 2350, y: 2200, type: 'exploding', options: { detectionRange: 130, fuseDuration: 1400, speed: 0.9, walkDist: 80 } },
        { x: 2700, y: 2050, type: 'exploding', options: { detectionRange: 140, fuseDuration: 1300, speed: 1.0, walkDist: 70 } },

        // ═══ Phase 4: Combined aerial ═══
        { x: 3300, y: 1700, type: 'flying', options: { amplitude: 70, speed: 1.5, walkDist: 140, color: 0x66ccff } },
        { x: 3600, y: 1500, type: 'flying', options: { amplitude: 50, speed: 1.8, walkDist: 100, color: 0xff66cc } },

        // ═══ Phase 5: All enemy gauntlet ═══
        { x: 4050, y: 1850, type: 'shield', options: { speed: 1.3, walkDist: 70, slamInterval: 2000, shieldColor: 0xff00ff } },
        { x: 4000, y: 1250, type: 'exploding', options: { detectionRange: 120, fuseDuration: 1200, speed: 0.8, walkDist: 50 } },
        { x: 4200, y: 1000, type: 'flying', options: { amplitude: 40, speed: 2.0, walkDist: 80, color: 0xffaa00 } },

        // ═══ Phase 6: Heavy defense ═══
        { x: 4500, y: 1250, type: 'shield', options: { speed: 1.5, walkDist: 120, slamInterval: 2000, shieldColor: 0x00ffff, bodyColor: 0x333355 } },
        { x: 5200, y: 950, type: 'exploding', options: { detectionRange: 150, fuseDuration: 1100, speed: 1.0, walkDist: 60 } },

        // ═══ Phase 7: Final guardians ═══
        { x: 5700, y: 800, type: 'flying', options: { amplitude: 60, speed: 1.6, walkDist: 100, color: 0xff0066 } },
        { x: 6100, y: 400, type: 'flying', options: { amplitude: 50, speed: 2.0, walkDist: 80, color: 0x00ff66 } },
        { x: 6450, y: 650 },
        { x: 6600, y: 650, type: 'shield', options: { speed: 1.0, walkDist: 60, slamInterval: 2500, shieldColor: 0xffff00 } }
    ],

    door: { x: 6700, y: 550, w: 60, h: 300 },
    button: { x: 6500, y: 695 },
    box: { x: 6350, y: 600, floating: true },
    goal: { x: 6750, y: 600 },

    lavaZones: [
        // Phase 3 lava pit
        { x: 2200, y: 2400, w: 500, h: 400 },
        // Phase 5 lava below climb
        { x: 3850, y: 2100, w: 400, h: 700 }
    ],

    windZones: [
        // Phase 4 sky updraft
        { x: 3100, y: 1500, w: 300, h: 600, forceX: 0, forceY: -2.0 },
        // Phase 5 cross-wind
        { x: 4050, y: 1000, w: 200, h: 500, forceX: 1.5, forceY: -1.0 },
        // Final ascent assist  
        { x: 6000, y: 400, w: 250, h: 400, forceX: 0, forceY: -1.5 }
    ],

    portals: [
        // Phase skip (for backtracking)
        { x1: 400, y1: 2550, x2: 2200, y2: 2200, color: 0x00ffff },
        // Mid-level warp
        { x1: 3100, y1: 2050, x2: 4350, y2: 1250, color: 0xff00ff }
    ],

    trampolines: [
        { x: 1000, y: 2150, w: 80 },
        { x: 2550, y: 2250, w: 80 },
        { x: 3650, y: 1700, w: 70 },
        { x: 3900, y: 1900, w: 70 },
        { x: 4350, y: 1300, w: 70 },
        { x: 5650, y: 1050, w: 70 },
        { x: 6250, y: 700, w: 80 }
    ],

    asteroids: [
        // Cosmic hazards in nexus core
        { x: 4600, y: 1100, radius: 25, speed: 0.001 },
        { x: 5000, y: 900, radius: 30, speed: 0.0012 },
        { x: 5500, y: 750, radius: 20, speed: 0.0015 }
    ],

    speedrunTargets: { gold: 100, silver: 140, bronze: 180 }
};

export default Level20_TheNexus;
